import React from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import Layout from '../../components/Layout';
import Container from '../../components/Container';
import { FaWindows, FaApple, FaLinux, FaBook, FaQuestion, FaTools, FaInfoCircle, FaGlobe, FaChevronRight } from 'react-icons/fa';
import Footer from '../../components/Footer';

const PageWrapper = styled.div`
  min-height: 100vh;
  width: 100%;
  background: 
    radial-gradient(circle at 20% 50%, rgba(110, 168, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(45, 108, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 40% 80%, rgba(58, 143, 255, 0.08) 0%, transparent 50%),
    linear-gradient(135deg, #0a0f2e 0%, #1a2451 50%, #0e1633 100%);
  position: relative;
  overflow: hidden;
  padding-bottom: 0;
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0.03;
  background-image: 
    linear-gradient(45deg, #6ea8ff 25%, transparent 25%),
    linear-gradient(-45deg, #6ea8ff 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #6ea8ff 75%),
    linear-gradient(-45deg, transparent 75%, #6ea8ff 75%);
  background-size: 60px 60px;
  background-position: 0 0, 0 30px, 30px -30px, -30px 0px;
  pointer-events: none;
`;

const ContentContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 6rem 1.5rem 4rem 1.5rem;
  position: relative;
  z-index: 2;
  
  @media (max-width: 768px) {
    padding: 4rem 1.5rem 3rem 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 3rem 1rem 2rem 1rem;
  }
`;

const Section = styled.div`
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    margin-bottom: 2.5rem;
  }
`;

const SectionHeader = styled.div`
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid rgba(45, 108, 255, 0.2);
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  
  &::before {
    content: '';
    display: block;
    width: 4px;
    height: 28px;
    background: linear-gradient(135deg, #6ea8ff 0%, #3a8fff 100%);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const FAQBadge = styled.span`
  display: inline-block;
  background: linear-gradient(135deg, #6ea8ff 0%, #3a8fff 100%);
  color: #fff;
  font-size: 0.7rem;
  font-weight: 600;
  border-radius: 12px;
  padding: 0.25rem 0.7rem;
  margin-left: 0.8rem;
  box-shadow: 0 4px 12px rgba(45, 108, 255, 0.4);
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const SectionDescription = styled.p`
  color: #b6d0ff;
  font-size: 1rem;
  margin: 0;
  line-height: 1.5;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

const LinksList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const DocumentLink = styled.a`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.2rem;
  background: rgba(20, 30, 60, 0.4);
  border: 1px solid rgba(45, 108, 255, 0.15);
  border-radius: 12px;
  text-decoration: none;
  color: #fff;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(20, 30, 60, 0.7);
    border-color: rgba(45, 108, 255, 0.4);
    transform: translateX(8px);
    box-shadow: 0 4px 16px rgba(45, 108, 255, 0.2);
  }
`;

const LinkIcon = styled.div`
  font-size: 1.3rem;
  color: #6ea8ff;
  flex-shrink: 0;
  transition: transform 0.3s ease;
  
  ${DocumentLink}:hover & {
    transform: scale(1.1);
  }
`;

const LinkContent = styled.div`
  flex: 1;
`;

const LinkTitle = styled.h3`
  font-size: 1.1rem;
  color: #fff;
  margin: 0 0 0.3rem 0;
  font-weight: 600;
  transition: color 0.3s ease;
  
  ${DocumentLink}:hover & {
    color: #b6d0ff;
  }
`;

const LinkDescription = styled.p`
  font-size: 0.9rem;
  color: #b6d0ff;
  margin: 0;
  opacity: 0.8;
  line-height: 1.4;
  transition: opacity 0.3s ease;
  
  ${DocumentLink}:hover & {
    opacity: 1;
  }
`;

const LinkArrow = styled(FaChevronRight)`
  color: #6ea8ff;
  font-size: 0.9rem;
  opacity: 0;
  transition: all 0.3s ease;
  
  ${DocumentLink}:hover & {
    opacity: 1;
    transform: translateX(4px);
  }
`;

const DocsPage = () => {
  const sections = [
    {
      title: 'Frequently Asked Questions',
      description: 'Find answers to common questions about Account Warmer.',
      showBadge: true,
      items: [
        {
          title: 'FAQ',
          description: 'Frequently asked questions and answers',
          icon: <FaQuestion />,
          link: '/faq',
        },
      ],
    },
    {
      title: 'About Account Warmer',
      description: 'Learn how Account Warmer works and what it can do for you.',
      items: [
        {
          title: 'How It Works',
          description: 'Understand the core functionality and automation process',
          icon: <FaInfoCircle />,
          link: '/docs/how-it-works',
        },
        {
          title: 'Getting Started',
          description: 'Quick start guide for new users',
          icon: <FaBook />,
          link: '/docs/getting-started',
        },
        {
          title: 'Supported Websites',
          description: 'View all supported websites by region for the initial warming session',
          icon: <FaGlobe />,
          link: '/docs/supported-websites',
        },
      ],
    },
    {
      title: 'Installation Guides',
      description: 'Step-by-step instructions for installing Account Warmer on different platforms.',
      items: [
        {
          title: 'Windows Installation',
          description: 'Install Account Warmer on Windows 10 and newer',
          icon: <FaWindows />,
          link: '/docs/install/windows',
        },
        {
          title: 'macOS Installation',
          description: 'Install Account Warmer on macOS (Intel & Apple Silicon)',
          icon: <FaApple />,
          link: '/docs/install/macos',
        },
        {
          title: 'Linux Installation',
          description: 'Install Account Warmer on Linux distributions',
          icon: <FaLinux />,
          link: '/docs/install/linux',
        },
      ],
    },
    {
      title: 'Help & Support',
      description: 'Find solutions to common issues and get help when needed.',
      items: [
        {
          title: 'Troubleshooting',
          description: 'Solutions for common issues and problems',
          icon: <FaTools />,
          link: '/docs/troubleshooting',
        },
        {
          title: 'Contact Support',
          description: 'Get help from our support team via Telegram',
          icon: <FaTools />,
          link: '/docs/contact-support',
        },
      ],
    },
  ];

  return (
    <Layout>
      <PageWrapper>
        <BackgroundPattern />
        <ContentContainer>
          {sections.map((section, index) => (
            <Section key={index}>
              <SectionHeader>
                <SectionTitle>
                  {section.title}
                  {section.showBadge && <FAQBadge>Popular</FAQBadge>}
                </SectionTitle>
                <SectionDescription>{section.description}</SectionDescription>
              </SectionHeader>
              
              <LinksList>
                {section.items.map((item, itemIndex) => (
                  <DocumentLink
                    key={itemIndex}
                    href={item.link}
                    target={item.link && item.link.startsWith('http') ? '_blank' : undefined}
                    rel={item.link && item.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                  >
                    <LinkIcon>{item.icon}</LinkIcon>
                    <LinkContent>
                      <LinkTitle>{item.title}</LinkTitle>
                      <LinkDescription>{item.description}</LinkDescription>
                    </LinkContent>
                    <LinkArrow />
                  </DocumentLink>
                ))}
              </LinksList>
            </Section>
          ))}
        </ContentContainer>
        <Footer />
      </PageWrapper>
    </Layout>
  );
};

export default DocsPage;